import { Suspense, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { DemoProvider } from './contexts/DemoContext';
import { OrderProvider } from './contexts/OrderContext';
import { TrainingProvider } from './contexts/TrainingContext';
import Header from './components/common/Header';
import Navigation from './components/common/Navigation';
import LoadingSpinner from './components/common/LoadingSpinner';
import IdleWarningModal from './components/common/IdleWarningModal';
import { PermissionProtectedRoute } from './components/common/ProtectedRoute';
import { TokenGuard } from './components/common/TokenGuard';
import InstallPWA from './components/InstallPWA';
import DemoDetailsModal from './components/demos/DemoDetailsModal';
import LoginForm from './components/auth/LoginForm';
import Dashboard from './pages/Dashboard';
import BookDemo from './pages/BookDemo';
//import Orders from './pages/Orders';
import NewOrders from './pages/NewOrders';
import { OrdersPage } from './features/orders';
import OrdersList from './pages/OrdersList';
import DemosList from './pages/DemosList';
import DocumentsPage from './pages/DocumentsPage';
import MyUploadsPage from './pages/MyUploadsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import TeamLeaderDashboard from './pages/teamLeader/TeamLeaderDashboard';
import UpdateBanner from './components/UpdateBanner';
import MyFeedbackPage from './pages/MyFeedbackPage';
import TrainingPage from './pages/TrainingPage';
import OnboardingTrainingPage from './pages/training/OnboardingTrainingPage';
import CreateAgentModal from './pages/CreateAgentPage';
import HostGiftsPage from './pages/HostGiftsPage';
import WhatsApp2Way from './features/whatsapp2way/components/WhatsApp2Way';
import WhatsAppDev from './features/wa_dev/components/WhatsAppDev';
import DraftAgreementPage from './pages/DraftAgreementPage';
import ProductShowcasePage from './features/product-showcase/components/ProductShowcasePage';
import DraftOrdersPage from './features/draft-orders/components/DraftOrdersPage';
import { GiftsCatalogPage } from './features/gifts';
import UpdateProductPrices from './pages/admin/UpdateProductPrices';
import CommissionsPage from './pages/CommissionsPage';

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Wrapper to handle modal routes
function ModalRoutes() {
  const location = useLocation();
  const background = location.state?.background;
  
  return (
    <>
      <Routes location={background || location}>
        <Route path="/login" element={<LoginForm />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/book-demo" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canBookDemos">
              <BookDemo />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/training" element={
          <ProtectedRoute>
            <OnboardingTrainingPage />
          </ProtectedRoute>
        } />

        <Route path="/orders" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canCreateOrders">
              <OrdersPage />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />

        <Route path="/new-orders" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canCreateOrders">
              <NewOrders />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/my-orders" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canViewOrders">
              <OrdersList />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/documents" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canViewDocuments">
              <DocumentsPage />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/my-uploads" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canViewMyUploads">
              <MyUploadsPage />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/my-feedback" element={
          <ProtectedRoute>
            <MyFeedbackPage />
          </ProtectedRoute>
        } />
        <Route path="/product-showcase" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canViewProductShowcase">
              <ProductShowcasePage />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/gifts-catalog" element={
          <ProtectedRoute>
            <GiftsCatalogPage />
          </ProtectedRoute>
        } />
        <Route path="/my-demos" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canBookDemos">
              <DemosList />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canAccessAdminPanel">
              <AdminDashboard />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/team-leader" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canAccessTeamLeaderPanel" requiredCategory="PLSSS">
              <TeamLeaderDashboard />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/host-gifts" element={
          <ProtectedRoute>
            <HostGiftsPage />
          </ProtectedRoute>
        } />
        <Route path="/whatsapp" element={
          <ProtectedRoute>
            <WhatsApp2Way />
          </ProtectedRoute>
        } />
        <Route path="/wadev" element={
          <ProtectedRoute>
            <WhatsAppDev />
          </ProtectedRoute>
        } />
        <Route path="/draft-agreements" element={
          <ProtectedRoute>
            <DraftAgreementPage />
          </ProtectedRoute>
        } />
        <Route path="/draft-orders" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canViewDraftOrders">
              <DraftOrdersPage />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/update-product-prices" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canAccessAdminPanel">
              <UpdateProductPrices />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/commissions" element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="canAccessAdminPanel">
              <CommissionsPage />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Modal Routes */}
      {background && (
        <Routes>
          <Route path="/demos/:demoId" element={
            <ProtectedRoute>
              <DemoDetailsModal />
            </ProtectedRoute>
          } />
        </Routes>
      )}
    </>
  );
}

function AppContent() {
  const { isAuthenticated } = useAuth();
  const [isCreateAgentModalOpen, setIsCreateAgentModalOpen] = useState(false);

  // Export modal control function to window for global access
  useEffect(() => {
    window.openCreateAgentModal = () => setIsCreateAgentModalOpen(true);
    window.closeCreateAgentModal = () => setIsCreateAgentModalOpen(false);
    
    return () => {
      delete window.openCreateAgentModal;
      delete window.closeCreateAgentModal;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7FBFC] via-[#F7FBFC] to-[#F7FBFC]">
      <UpdateBanner />
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-4">
        <Suspense fallback={<LoadingSpinner size="lg" className="py-20" />}>
          <ModalRoutes />
        </Suspense>
      </main>
      
      {/* Add Idle Warning Modal */}
      <IdleWarningModal />
      
      {/* Add Create Agent Modal */}
      <CreateAgentModal 
        isOpen={isCreateAgentModalOpen} 
        onClose={() => setIsCreateAgentModalOpen(false)} 
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <TokenGuard>
        <CartProvider>
          <DemoProvider>
            <OrderProvider>
              <TrainingProvider>
                <AppContent />
                <InstallPWA />
              </TrainingProvider>
            </OrderProvider>
          </DemoProvider>
        </CartProvider>
      </TokenGuard>
    </AuthProvider>
  );
}

export default App;
